import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { fetchData, postData, patchData, deleteData } from './api';
import { selectCurrentGroupId } from './groups_slice';

const isSeAdminUser = (state: RootState) => state.authentication.user?.burnout_role === 'se_admin';

const resolveGroupId = (state: RootState): number | undefined =>
  isSeAdminUser(state)
    ? selectCurrentGroupId(state) || undefined
    : (state.authentication.user as any)?.practice_group_ids?.[0];

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';
type ConnectionStatus = 'idle' | 'loading' | 'connected' | 'not_connected';

interface TremendousStatusPayload {
  connected: boolean;
  onboarding_started: boolean;
  onboarding_status: string | null;
  onboarding_status_label: string | null;
  rewards_enabled: boolean;
  points_per_dollar: number | null;
  minimum_redemption_points: number | null;
  maximum_redemption_points: number | null;
}

interface RedeemResponse {
  redemption_id: number;
  points_spent: number;
  amount_cents: number;
  status: string;
  link: string;
}

interface TremendousState {
  connectionStatus: ConnectionStatus;
  onboardingStarted: boolean;
  onboardingStatus: string | null;
  onboardingStatusLabel: string | null;
  rewardsEnabled: boolean;
  pointsPerDollar: number | null;
  minimumRedemptionPoints: number | null;
  maximumRedemptionPoints: number | null;
  redeemStatus: AsyncStatus;
  redeemLink: string | null;
  disconnectStatus: AsyncStatus;
  settingsUpdateStatus: AsyncStatus;
  error: string | null;
}

const initialState: TremendousState = {
  connectionStatus: 'idle',
  onboardingStarted: false,
  onboardingStatus: null,
  onboardingStatusLabel: null,
  rewardsEnabled: false,
  pointsPerDollar: null,
  minimumRedemptionPoints: null,
  maximumRedemptionPoints: null,
  redeemStatus: 'idle',
  redeemLink: null,
  disconnectStatus: 'idle',
  settingsUpdateStatus: 'idle',
  error: null,
};

export const fetchTremendousStatus = createAsyncThunk<
  TremendousStatusPayload,
  void,
  { state: RootState }
>(
  'tremendous/fetchStatus',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const seAdmin = isSeAdminUser(state);
    const groupId = resolveGroupId(state);
    if (seAdmin && !groupId) return rejectWithValue('No practice group selected');
    try {
      return (await fetchData({
        endpoint: 'rewards/connection_status',
        filters: seAdmin ? { group_id: groupId } : undefined,
      })) as TremendousStatusPayload;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch Tremendous status');
    }
  },
  {
    condition: (_, { getState }) => getState().tremendous.connectionStatus === 'idle',
  }
);

export const startOnboarding = createAsyncThunk<{ session_url: string }, void, { state: RootState }>(
  'tremendous/startOnboarding',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const seAdmin = isSeAdminUser(state);
    const groupId = resolveGroupId(state);
    if (seAdmin && !groupId) return rejectWithValue('No practice group selected');
    try {
      return (await postData({
        endpoint: 'tremendous/onboard',
        filters: seAdmin ? { group_id: groupId } : undefined,
      })) as { session_url: string };
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to start onboarding');
    }
  }
);

export const disconnectTremendous = createAsyncThunk<void, void, { state: RootState }>(
  'tremendous/disconnect',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const seAdmin = isSeAdminUser(state);
    const groupId = resolveGroupId(state);
    if (seAdmin && !groupId) return rejectWithValue('No practice group selected');
    try {
      await deleteData({
        endpoint: 'tremendous/disconnect',
        filters: seAdmin ? { group_id: groupId } : undefined,
      });
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to disconnect Tremendous');
    }
  }
);

export const updateRewardSettings = createAsyncThunk<
  { rewards_enabled: boolean; points_per_dollar: number; minimum_redemption_points: number; maximum_redemption_points: number },
  { rewards_enabled: boolean; points_per_dollar: number; minimum_redemption_points: number; maximum_redemption_points: number },
  { state: RootState }
>(
  'tremendous/updateRewardSettings',
  async (payload, { getState, rejectWithValue }) => {
    const groupId = resolveGroupId(getState());
    if (!groupId) return rejectWithValue('No practice group found');
    try {
      return (await patchData({
        endpoint: `group_reward_settings/${groupId}`,
        payload,
      })) as { rewards_enabled: boolean; points_per_dollar: number; minimum_redemption_points: number; maximum_redemption_points: number };
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to update reward settings');
    }
  }
);

export const redeemPoints = createAsyncThunk<RedeemResponse, number, { state: RootState }>(
  'tremendous/redeemPoints',
  async (pointsToRedeem, { getState, rejectWithValue }) => {
    const state = getState();
    const seAdmin = isSeAdminUser(state);
    const groupId = resolveGroupId(state);
    if (seAdmin && !groupId) return rejectWithValue('No practice group selected');
    try {
      return (await postData({
        endpoint: 'rewards/redeem',
        payload: { points_to_redeem: pointsToRedeem },
        filters: seAdmin ? { group_id: groupId } : undefined,
      })) as RedeemResponse;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error || err.message || 'Failed to redeem points');
    }
  },
  {
    condition: (_, { getState }) => getState().tremendous.redeemStatus !== 'loading',
  }
);

const tremendousSlice = createSlice({
  name: 'tremendous',
  initialState,
  reducers: {
    clearTremendousError: (state) => { state.error = null; },
    resetRedeemStatus: (state) => { state.redeemStatus = 'idle'; state.redeemLink = null; },
    resetSettingsUpdateStatus: (state) => { state.settingsUpdateStatus = 'idle'; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTremendousStatus.pending, (state) => {
        state.connectionStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchTremendousStatus.fulfilled, (state, action) => {
        state.connectionStatus = action.payload.connected ? 'connected' : 'not_connected';
        state.onboardingStarted = action.payload.onboarding_started;
        state.onboardingStatus = action.payload.onboarding_status;
        state.onboardingStatusLabel = action.payload.onboarding_status_label;
        state.rewardsEnabled = action.payload.rewards_enabled;
        state.pointsPerDollar = action.payload.points_per_dollar;
        state.minimumRedemptionPoints = action.payload.minimum_redemption_points;
        state.maximumRedemptionPoints = action.payload.maximum_redemption_points;
      })
      .addCase(fetchTremendousStatus.rejected, (state, action) => {
        state.connectionStatus = 'not_connected';
        state.error = (action.payload as string) ?? action.error.message ?? null;
      });

    builder
      .addCase(disconnectTremendous.pending, (state) => {
        state.disconnectStatus = 'loading';
        state.error = null;
      })
      .addCase(disconnectTremendous.fulfilled, (state) => {
        state.disconnectStatus = 'succeeded';
        state.connectionStatus = 'not_connected';
        state.onboardingStarted = false;
        state.onboardingStatus = null;
        state.onboardingStatusLabel = null;
        state.pointsPerDollar = null;
        state.minimumRedemptionPoints = null;
        state.maximumRedemptionPoints = null;
      })
      .addCase(disconnectTremendous.rejected, (state, action) => {
        state.disconnectStatus = 'failed';
        state.error = (action.payload as string) ?? action.error.message ?? null;
      });

    builder
      .addCase(updateRewardSettings.pending, (state) => {
        state.settingsUpdateStatus = 'loading';
        state.error = null;
      })
      .addCase(updateRewardSettings.fulfilled, (state, action) => {
        state.settingsUpdateStatus = 'succeeded';
        state.rewardsEnabled = action.payload.rewards_enabled;
        state.pointsPerDollar = action.payload.points_per_dollar;
        state.minimumRedemptionPoints = action.payload.minimum_redemption_points;
        state.maximumRedemptionPoints = action.payload.maximum_redemption_points;
      })
      .addCase(updateRewardSettings.rejected, (state, action) => {
        state.settingsUpdateStatus = 'failed';
        state.error = (action.payload as string) ?? action.error.message ?? null;
      });

    builder
      .addCase(redeemPoints.pending, (state) => {
        state.redeemStatus = 'loading';
        state.error = null;
      })
      .addCase(redeemPoints.fulfilled, (state, action) => {
        state.redeemStatus = 'succeeded';
        state.redeemLink = action.payload.link;
      })
      .addCase(redeemPoints.rejected, (state, action) => {
        state.redeemStatus = 'failed';
        state.error = (action.payload as string) ?? action.error.message ?? null;
      });
  },
});

export const { clearTremendousError, resetRedeemStatus, resetSettingsUpdateStatus } = tremendousSlice.actions;

export const getConnectionStatus = (state: TremendousState) => state.connectionStatus;
export const getOnboardingStarted = (state: TremendousState) => state.onboardingStarted;
export const getOnboardingStatus = (state: TremendousState) => state.onboardingStatus;
export const getOnboardingStatusLabel = (state: TremendousState) => state.onboardingStatusLabel;
export const getRewardsEnabled = (state: TremendousState) => state.rewardsEnabled;
export const getPointsPerDollar = (state: TremendousState) => state.pointsPerDollar;
export const getMinimumRedemptionPoints = (state: TremendousState) => state.minimumRedemptionPoints;
export const getMaximumRedemptionPoints = (state: TremendousState) => state.maximumRedemptionPoints;
export const getRedeemStatus = (state: TremendousState) => state.redeemStatus;
export const getRedeemLink = (state: TremendousState) => state.redeemLink;
export const getDisconnectStatus = (state: TremendousState) => state.disconnectStatus;
export const getSettingsUpdateStatus = (state: TremendousState) => state.settingsUpdateStatus;
export const getTremendousError = (state: TremendousState) => state.error;

export default tremendousSlice.reducer;
